/**
 * Andromeda Score Component
 * Displays Meta's Andromeda optimization score for generated content
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Zap, 
  TrendingUp, 
  Target, 
  Eye, 
  Sparkles,
  ChevronDown,
  ChevronUp,
  Info,
  CheckCircle2,
  AlertTriangle,
  Lightbulb
} from "lucide-react";
import { apiClient } from "@/lib/api";

// Score gauge component
function ScoreGauge({ score, grade }) {
  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'text-green-400';
    if (grade.startsWith('B')) return 'text-blue-400';
    if (grade.startsWith('C')) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'stroke-green-400';
    if (score >= 60) return 'stroke-blue-400';
    if (score >= 40) return 'stroke-yellow-400';
    return 'stroke-red-400';
  };

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-32 h-32 mx-auto">
      <svg className="w-full h-full transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="64"
          cy="64"
          r="45"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-white/10"
        />
        {/* Score circle */}
        <motion.circle
          cx="64"
          cy="64"
          r="45"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          className={getScoreColor(score)}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          style={{ strokeDasharray: circumference }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span 
          className="text-3xl font-bold"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
        >
          {score}
        </motion.span>
        <span className={`text-sm font-semibold ${getGradeColor(grade)}`}>{grade}</span>
      </div>
    </div>
  );
}

// Factor bar component
function FactorBar({ factor, expanded }) {
  const getBarColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="py-2">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium">{factor.name}</span>
        <span className="text-sm text-viraly-muted">{factor.score}/100</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${getBarColor(factor.score)} rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${factor.score}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      {expanded && factor.tips && factor.tips.length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-2"
        >
          {factor.tips.map((tip, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-viraly-muted mt-1">
              <Lightbulb className="w-3 h-3 text-yellow-400 flex-shrink-0 mt-0.5" />
              <span>{tip}</span>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

export default function AndromedaScore({ 
  caption = "",
  hashtags = [],
  hasCta = false,
  ctaText = "",
  imageUrl = "",
  videoUrl = "",
  contentType = "image",
  platform = "instagram",
  hasBranding = false,
  textOverlayPercent = null,
  aspectRatio = null,
  variationCount = 1,
  listingType = null
}) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const calculateScore = async () => {
      if (!caption) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const hookText = caption.split('\n')[0] || caption.substring(0, 100);
        
        const response = await apiClient.post('/content/andromeda-score', {
          caption,
          hashtags,
          has_cta: hasCta,
          cta_text: ctaText,
          image_url: imageUrl,
          video_url: videoUrl,
          content_type: contentType,
          platform,
          has_branding: hasBranding,
          text_overlay_percent: textOverlayPercent,
          aspect_ratio: aspectRatio,
          hook_text: hookText,
          variation_count: variationCount,
          listing_type: listingType
        });

        setScore(response.data);
        setError(null);
      } catch (err) {
        console.error("Andromeda score error:", err);
        setError("Could not calculate score");
      } finally {
        setLoading(false);
      }
    };

    calculateScore();
  }, [caption, hashtags, hasCta, platform, contentType, variationCount]);

  if (loading) {
    return (
      <div className="rounded-xl bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 p-4">
        <div className="flex items-center justify-center gap-3">
          <div className="animate-spin">
            <Sparkles className="w-5 h-5 text-purple-400" />
          </div>
          <span className="text-sm text-viraly-muted">Calculating Andromeda Score...</span>
        </div>
      </div>
    );
  }

  if (error || !score) {
    return null; // Silently fail if no score
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 p-4"
      data-testid="andromeda-score-card"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <Target className="w-4 h-4 text-purple-400" />
        </div>
        <div>
          <h3 className="font-semibold text-sm flex items-center gap-2">
            Andromeda Score
            {score.is_andromeda_optimized && (
              <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">
                Optimized
              </span>
            )}
          </h3>
          <p className="text-xs text-viraly-muted">Meta's algorithm optimization</p>
        </div>
      </div>

      {/* Score Gauge */}
      <ScoreGauge score={score.overall_score} grade={score.grade} />

      {/* Summary */}
      <p className="text-sm text-center mt-4 mb-4">{score.summary}</p>

      {/* Top Improvements */}
      {score.top_improvements && score.top_improvements.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-white/5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-viraly-orange" />
            <span className="text-xs font-semibold">Quick Wins</span>
          </div>
          {score.top_improvements.map((tip, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-viraly-muted mt-1">
              <CheckCircle2 className="w-3 h-3 text-viraly-orange flex-shrink-0 mt-0.5" />
              <span>{tip}</span>
            </div>
          ))}
        </div>
      )}

      {/* Expandable Details */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-center gap-1 text-xs text-viraly-muted hover:text-white transition-colors py-2"
        data-testid="andromeda-expand-btn"
      >
        {expanded ? (
          <>
            <ChevronUp className="w-4 h-4" />
            Hide Details
          </>
        ) : (
          <>
            <ChevronDown className="w-4 h-4" />
            View All Factors
          </>
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4 border-t border-white/10">
              {score.factors.map((factor, i) => (
                <FactorBar key={i} factor={factor} expanded={expanded} />
              ))}
            </div>

            {/* Platform Tips */}
            {score.platform_specific_tips && score.platform_specific_tips.length > 0 && (
              <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  <span className="text-xs font-semibold capitalize">{platform} Tips</span>
                </div>
                {score.platform_specific_tips.map((tip, i) => (
                  <p key={i} className="text-xs text-viraly-muted mt-1">• {tip}</p>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
